import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ulid } from 'ulid';
import ErrorPage from '@/components/shared/ErrorPage';
import LoadingContainer from '@/components/shared/LoadingContainer';
import { constants } from '@/constants';
import { useAppointmentInfoQuery } from '@/features/loadAppointment/api/getAppointmentInformation';
import useAppointmentStore from '@/store/AppointmentStore';
import useSessionStore from '@/store/SessionStore';

function LoadAppointment() {
  const [searchParams, _] = useSearchParams();
  const navigate = useNavigate();
  const {
    setSelectedDestination,
    setSearchedDestination,
    setSelectedAgent,
    setAppointmentId,
    setSelectedAppointmentType,
    setContactInfo,
    setSelectedStartTime,
    setSelectedEndTime,
    setCustomerId,
    setConfirmationId,
    setConfirmedAppointment,
    setSelectedBranch,
  } = useAppointmentStore();

  const { setSessionSource, setSessionId, sessionId } = useSessionStore();

  const [isDoneFetching, setIsDoneFetching] = useState(false);

  const { data, isError, refetch } = useAppointmentInfoQuery(
    searchParams.get('appointmentId') ?? '',
  );

  useEffect(() => {
    if (data) {
      setIsDoneFetching(true);
      if (data.status === 'Canceled') {
        return;
      }
      const appointmentType =
        data.appointmentType === 'In Person' ? 'in-branch' : 'online';

      if (appointmentType === 'in-branch') {
        setSelectedBranch({
          branchId: data.serviceTerritoryId,
          branchName: data.serviceTerritoryName || '',
          branchAddress: '',
          distance: 0,
        });
      }
      setConfirmedAppointment({
        agentInfo: {
          agentRecordId: data.agentUserId,
          resourceId: data.appointmentServiceResourceId,
          serviceTerritoryId: data.serviceTerritoryId,
          name: data.name,
          image: data.image,
        },
        selectedDate: new Date(data.schedStartTime),
        selectedStartTime: data.schedStartTime,
        selectedEndTime: data.schedEndTime,
      });
      setSelectedDestination({
        destinationApiName: data.destination,
        workTypeId: data.workTypeId,
        name: data.destination,
      });
      if (data.searchedDestination) {
        setSearchedDestination(data.searchedDestination);
      }
      setSelectedAgent({
        agentRecordId: data.agentUserId,
        resourceId: data.appointmentServiceResourceId,
        serviceTerritoryId: data.serviceTerritoryId,
        name: data.name,
        image: data.image,
      });
      setAppointmentId(data.appointmentId);
      setSelectedAppointmentType(appointmentType);
      setContactInfo({
        email: data.email,
        communicationPreference:
          appointmentType === 'in-branch' ? 'In Person' : data.appointmentType,
      });
      setSelectedStartTime(data.schedStartTime);
      setSelectedEndTime(data.schedEndTime);
      setConfirmationId(data.appointmentId);
      setCustomerId(data.customerId);
      setTimeout(() => {
        setSessionSource('email');
        if (!sessionId) {
          const newSessionId = ulid();
          setSessionId(newSessionId);
        }
        navigate('/confirmation?source=email');
      }, 250);
    }
  }, [
    data,
    setConfirmedAppointment,
    setSelectedDestination,
    setSelectedAgent,
    setAppointmentId,
    setSelectedAppointmentType,
    setContactInfo,
    setSelectedStartTime,
    setSelectedEndTime,
    setConfirmationId,
    setCustomerId,
    setSessionSource,
    setSelectedBranch,
    sessionId,
    setSessionId,
    navigate,
  ]);

  if (isError) {
    return <ErrorPage onClickTryAgain={refetch} />;
  }

  if (data?.status === 'Canceled') {
    return (
      <ErrorPage
        onClickTryAgain={() => {
          window.location.replace(`${constants.APP_BASE_URL}`);
        }}
        message={'This appointment has been cancelled.'}
        imageUrl='/travel/agent/scheduler/canceledAppointmentImage.png'
        buttonLabel='Start over'
      />
    );
  }

  return (
    <LoadingContainer
      message='Retrieving your appointment details...'
      radiosInfo={[
        {
          radioMessage: 'Retrieving your advisor information',
          isChecked: isDoneFetching,
        },
      ]}
    />
  );
}
export default LoadAppointment;
